const EMAIL_VERIFICATION_STORAGE_KEY = "nommia.emailVerificationEmail";
const EMAIL_VERIFICATION_SOURCES = ["login", "register"] as const;

export type EmailVerificationSource =
  (typeof EMAIL_VERIFICATION_SOURCES)[number];

type PendingEmailVerificationOptions = {
  redirectPath?: string;
  otpSentAt?: number;
  source?: EmailVerificationSource;
};

export type PendingEmailVerificationData = {
  email: string;
  redirectPath?: string;
  otpSentAt?: number;
  source?: EmailVerificationSource;
};

const isEmailVerificationSource = (
  value: unknown
): value is EmailVerificationSource =>
  typeof value === "string" &&
  EMAIL_VERIFICATION_SOURCES.includes(value as EmailVerificationSource);

const canUseStorage = () => typeof window !== "undefined";

const normalizeEmail = (email?: string) => {
  if (!email) return "";
  const trimmed = email.trim();
  return trimmed.length > 0 ? trimmed : "";
};

const serializePayload = (
  email: string,
  options?: PendingEmailVerificationOptions
) =>
  JSON.stringify({
    email,
    ...(options?.redirectPath ? { redirectPath: options.redirectPath } : {}),
    ...(typeof options?.otpSentAt === "number"
      ? { otpSentAt: options.otpSentAt }
      : {}),
    ...(options?.source ? { source: options.source } : {}),
  });

const parsePayload = (
  raw: string | null
): PendingEmailVerificationData | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as {
      email?: unknown;
      redirectPath?: unknown;
      otpSentAt?: unknown;
      source?: unknown;
    };

    if (typeof parsed.email === "string" && parsed.email.trim().length > 0) {
      return {
        email: parsed.email,
        redirectPath:
          typeof parsed.redirectPath === "string"
            ? parsed.redirectPath
            : undefined,
        otpSentAt:
          typeof parsed.otpSentAt === "number" &&
          Number.isFinite(parsed.otpSentAt)
            ? parsed.otpSentAt
            : undefined,
        source: isEmailVerificationSource(parsed.source)
          ? parsed.source
          : undefined,
      };
    }
  } catch {
    const fallbackEmail = normalizeEmail(raw);
    if (fallbackEmail) {
      return { email: fallbackEmail };
    }
  }

  return null;
};

export const setPendingEmailVerification = (
  email?: string,
  options?: PendingEmailVerificationOptions
) => {
  const normalizedEmail = normalizeEmail(email);

  if (!canUseStorage() || !normalizedEmail) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      EMAIL_VERIFICATION_STORAGE_KEY,
      serializePayload(normalizedEmail, options)
    );
  } catch {
    // Ignore storage failures so auth flow can continue.
  }
};

export const getPendingEmailVerification =
  (): PendingEmailVerificationData | null => {
    if (!canUseStorage()) {
      return null;
    }

    try {
      const rawValue = window.sessionStorage.getItem(
        EMAIL_VERIFICATION_STORAGE_KEY
      );
      return parsePayload(rawValue);
    } catch {
      return null;
    }
  };

export const clearPendingEmailVerification = () => {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(EMAIL_VERIFICATION_STORAGE_KEY);
  } catch {
    // Ignore storage failures so logout/cleanup can continue.
  }
};

export { EMAIL_VERIFICATION_STORAGE_KEY };
